import { createDrawerNavigator } from '@react-navigation/drawer';
import { useRoute } from '@react-navigation/native';
import { Icon, Text } from '@ui-kitten/components';
import dayjs from 'dayjs';
import lodashFilter from 'lodash/filter';
import lodashIsEmpty from 'lodash/isEmpty';
import lodashIsUndefined from 'lodash/isUndefined';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Linking, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';

import Button from '../../Components/Button';
import ComingSoon from '../../Components/ComingSoon';
import Header from '../../Components/Header';
import LeftDrawerContent from '../../Components/LeftDrawerContent';
import SchedulePicker from '../../Components/SchedulePicker';
import TabBarBasket from '../../Components/SVG/TabBarBasket';
import TabBarFeed from '../../Components/SVG/TabBarFeed';
import TabBarHome from '../../Components/SVG/TabBarHome';
import TabBarProfile from '../../Components/SVG/TabBarProfile';
import { ModalManager } from '../../Components/Web/Modal/hooks/useModalManager';
import { MODALPROMPT } from '../../Components/Web/Modal/ModalPrompt/config';
import useModalPrompt from '../../Components/Web/Modal/ModalPrompt/hooks/useModalPrompt';
import Search from '../../Components/Web/Search';
import SearchReservation from '../../Components/Web/SearchReservation';
import constants from '../../Config/constants';
import { capitalize } from '../../Helper';
import {
  getCartTerm,
  getIsEnabledReservation,
} from '../../Helper/RemoteConfig';
import UrlParameterHelper from '../../Helper/UrlParameter';
import useMealPlan from '../../Hooks/useMealPlan';
import useSetUserData from '../../Hooks/useSetUserData';
import { useMainContext } from '../../MainContext';
import { setAddressSelection as setAddressNormalSelection } from '../../RTK/checkout';
import { checkoutFlagSelector } from '../../RTK/checkout/selectors';
import { whenFilterSelector } from '../../RTK/filter/selectors';
import { setMealPlanAddressSelection } from '../../RTK/mealPlan';
import { selectMealPlanCheckoutFlag } from '../../RTK/mealPlan/selectors';
import { shopSelector } from '../../RTK/shop/selectors';
import { logout } from '../../RTK/user';
import {
  activeAddressSelector,
  isGuestSelector,
  isLoggedInSelector,
} from '../../RTK/user/selectors';
import { selectPage, selectRoute } from '../../RTK/utility/selectors';
import { Profile } from '../../Screens/Account';
import Basket from '../../Screens/Basket';
import Checkout from '../../Screens/Checkout';
import CheckoutWebview from '../../Screens/Checkout/CheckoutWebview';
import FeedDescription from '../../Screens/Feed/view/Description';
import { myFavorites } from '../../Screens/Home/sections';
import SeeAll from '../../Screens/SeeAll';
import Store from '../../Screens/Store';
import ThemeColor from '../../Theme/colors';
import ThemeStyle from '../../Theme/styles';

import HomeNavigator from '../HomeNavigator';
import routeList, { routeGroup } from '../list';
import ReservationNavigator from '../ReservationNavigator';

import { ids, styles } from './';

const LeftDrawer = createDrawerNavigator();
const iconSize = 20;
const svgIconSize = 35;
const svgStrokeWidth = 45;
const LeftDrawerScreen = () => {
  const dispatch = useDispatch<any>();
  const drawerRoute = useRoute<any>();
  const timeoutRef = useRef<NodeJS.Timeout>();
  const { showModalPrompt } = useModalPrompt();
  const isSetUserData = useSetUserData();
  const { isRemoteConfigFetching } = useMainContext();
  const page = useSelector(selectPage);
  const route = useSelector(selectRoute);
  const isGuest = useSelector(isGuestSelector);
  const shopData = useSelector(shopSelector);
  const isLoggedIn = useSelector(isLoggedInSelector);
  const normalAddressSelection = useSelector(checkoutFlagSelector);
  const mealPlanAddressSelection = useSelector(selectMealPlanCheckoutFlag);
  const whenFilter = useSelector(whenFilterSelector);
  const userFavorites = useSelector(
    (state: any) => state.favourite?.favouriteData,
  );
  const deepLinkingData = useSelector(
    // @ts-ignore - must create selector for this
    (state) => state.noSession.deepLinkingData,
  );
  const activeAddress = useSelector(activeAddressSelector);
  const { isMealPlan } = useMealPlan();
  const [dateVisible, setDateVisible] = useState(false);

  useEffect(() => {
    clearTimeout(timeoutRef.current);
    const _maybeShowAddressModal = async () => {
      const url = await Linking.getInitialURL();
      const pathname = UrlParameterHelper.getUrlStringPath(url) as routeList;
      const isReservationRoute = routeGroup.isReservation.includes(pathname);
      if (!isReservationRoute) {
        // show only guest address selector on marketplace not on reservation
        timeoutRef.current = setTimeout(
          () => ModalManager.show(MODALPROMPT.deliveryAddress),
          250,
        );
      }
    };
    if (
      !isRemoteConfigFetching &&
      !isLoggedIn &&
      lodashIsEmpty(activeAddress)
    ) {
      _maybeShowAddressModal();
    }
    if (normalAddressSelection || mealPlanAddressSelection) {
      dispatch(setAddressNormalSelection(false));
      dispatch(setMealPlanAddressSelection(false));
    }
  }, [
    activeAddress,
    deepLinkingData,
    isLoggedIn,
    isRemoteConfigFetching,
    mealPlanAddressSelection,
    normalAddressSelection,
    dispatch,
  ]);

  const _toggleAddressList = () => {
    ModalManager.show(MODALPROMPT.listAddress);
  };

  const Drawerheader = useCallback(
    (props) => {
      if (page.isCheckoutWebview) return null;
      return (
        <View>
          {!page.isCheckoutWebview && (
            <Header
              {...props}
              disableMenu={!isSetUserData && isRemoteConfigFetching}
            />
          )}

          <View
            dataSet={{ media: ids.topLayoutWithOutCredentials }}
            style={styles.topLayoutWithOutCredentials}
          >
            {!page.isCheckout && !page.isStore && (
              <View
                dataSet={{ media: ids.searchContainer }}
                style={styles.searchContainer}
              >
                {page.isReservation ? <SearchReservation /> : <Search />}
              </View>
            )}
            {!page.isCheckout && !page.isReservation && (
              <View style={styles.addressAndDateFilterContainer}>
                <Button
                  disabled={isMealPlan}
                  style={ThemeStyle.flex1}
                  onPress={() => setDateVisible(true)}
                  plain
                >
                  <View style={ThemeStyle.flexDirectionRowCenterCenter}>
                    <Text category="p1" numberOfLines={1}>
                      {lodashIsUndefined(whenFilter)
                        ? 'ASAP'
                        : dayjs(whenFilter?.value).format(
                            constants.DATE_TIME_FILTER_DISPLAY,
                          )}
                    </Text>
                    <Icon
                      name="chevron-down"
                      fill={ThemeColor.black}
                      style={{ width: iconSize, height: iconSize }}
                    />
                  </View>
                </Button>
                <Button
                  style={ThemeStyle.flex1}
                  onPress={_toggleAddressList}
                  plain
                >
                  <View
                    dataSet={{ media: ids.popContentTriggerButton }}
                    style={styles.popContentTriggerButton}
                  >
                    <Text category="p1" numberOfLines={1}>
                      {!lodashIsEmpty(activeAddress)
                        ? activeAddress.formatted_address
                        : 'Address'}
                    </Text>
                    <Icon
                      name="chevron-down"
                      fill={ThemeColor.black}
                      style={{ width: iconSize, height: iconSize }}
                    />
                  </View>
                </Button>
              </View>
            )}
          </View>
        </View>
      );
    },
    [
      activeAddress,
      isMealPlan,
      isRemoteConfigFetching,
      isSetUserData,
      page.isCheckout,
      page.isCheckoutWebview,
      page.isReservation,
      page.isStore,
      whenFilter,
    ],
  );

  const screenOptions = {
    drawerPosition: 'left',
    // intended not equalto use strict equality (`===`) here because `exclusiveBooking` can be a string '1' from URL params
    header: route?.params?.exclusiveBooking == '1' ? () => null : Drawerheader,
    sceneContainerStyle: {
      backgroundColor: '#fff',
    },
  };

  const toggle = () =>
    showModalPrompt(MODALPROMPT.prompt, {
      title: 'Confirm Logout',
      message: `Are you sure you want to logout?`,
      buttons: [
        {
          size: 'small',
          status: 'danger',
          text: 'Yes',
          onPress: confirmLogout,
        },
        { size: 'small', text: 'No' },
      ],
    });

  const confirmLogout = async () => {
    toggle();
    dispatch(logout());
  };

  const leftDrawerContent = [
    {
      text: 'Home',
      value: routeList.HOME_TAB,
      SideBarIcon: ({ color }) => (
        <TabBarHome
          width={svgIconSize}
          height={svgIconSize}
          strokeWidth={svgStrokeWidth}
          color={color}
        />
      ),
    },
    {
      text: 'Orders',
      value: routeList.BASKET_TAB,
      SideBarIcon: ({ color }) => (
        <TabBarBasket
          width={svgIconSize}
          height={svgIconSize}
          strokeWidth={svgStrokeWidth}
          color={color}
        />
      ),
      hide: isGuest || !isLoggedIn,
    },
    {
      text: 'Account',
      value: routeList.PROFILE,
      SideBarIcon: ({ color }) => (
        <TabBarProfile
          width={svgIconSize}
          height={svgIconSize}
          strokeWidth={svgStrokeWidth}
          color={color}
        />
      ),
      hide: isGuest || !isLoggedIn,
    },
    {
      text: 'Saved Stores',
      value: routeList.SEE_ALL,
      SideBarIcon: ({ color }) => (
        <Icon name="heart-outline" style={styles.drawerIcon} fill={color} />
      ),
      params: {
        title: 'Saved Stores',
        ...myFavorites.tag,
      },
      hide: lodashIsEmpty(userFavorites),
    },
    {
      text: 'Reservations',
      value: routeList.RESERVATION_TAB,
      SideBarIcon: ({ color }) => (
        <TabBarFeed
          width={svgIconSize}
          height={svgIconSize}
          strokeWidth={svgStrokeWidth}
          color={color}
        />
      ),
      hide: !getIsEnabledReservation(),
    },
    {
      text: 'Sign Out',
      value: routeList.ACCOUNT_TAB,
      SideBarIcon: ({ color }) => (
        <Icon name="log-out-outline" style={styles.drawerIcon} fill={color} />
      ),
      onPressItem: toggle,
      hide: isGuest || !isLoggedIn,
    },
  ];

  return (
    <Fragment>
      <LeftDrawer.Navigator
        id="LeftDrawer"
        initialRouteName={routeList.HOME_TAB}
        useLegacyImplementation
        screenOptions={screenOptions}
        drawerContent={(props) => (
          <LeftDrawerContent
            data={lodashFilter(leftDrawerContent, (d) => !d.hide)}
            {...props}
          />
        )}
      >
        <LeftDrawer.Screen
          name={routeList.HOME_TAB}
          component={HomeNavigator}
          options={{
            title: 'Home',
            iconName: 'home',
            unmountOnBlur: true,
          }}
        />
        <LeftDrawer.Screen
          name={routeList.BASKET_TAB}
          component={Basket}
          options={{
            title: capitalize(getCartTerm()),
            iconName: 'shopping-bag',
            unmountOnBlur: true,
          }}
        />

        <LeftDrawer.Screen
          name={routeList.PROFILE}
          component={Profile}
          options={{
            title: 'Account',
            iconName: 'home',
            unmountOnBlur: true,
          }}
        />

        <LeftDrawer.Screen
          name={routeList.FEED_DESCRIPTION}
          component={FeedDescription}
          options={{
            unmountOnBlur: true,
          }}
        />
        <LeftDrawer.Screen
          name={routeList.SEE_ALL}
          options={{ unmountOnBlur: true }}
          component={SeeAll}
        />

        <LeftDrawer.Screen
          name={routeList.STORE}
          options={{
            unmountOnBlur: true,
          }}
          component={Store}
        />
        <LeftDrawer.Screen
          name={routeList.RESERVATION_TAB}
          options={{
            unmountOnBlur: true,
          }}
          component={
            !isRemoteConfigFetching && getIsEnabledReservation()
              ? ReservationNavigator
              : ComingSoon
          }
        />
        <LeftDrawer.Screen
          name={routeList.CHECKOUT}
          options={{ unmountOnBlur: true }}
          component={Checkout}
        />
        <LeftDrawer.Screen
          name={routeList.CHECKOUT_WEBVIEW}
          options={{ unmountOnBlur: true }}
          component={CheckoutWebview}
        />
      </LeftDrawer.Navigator>

      {
        //To render again when opening the modal
        //So it will prevent to retain the current state
        //It will depends on the current whenfilter selector
        dateVisible && (
          <SchedulePicker
            storeId={shopData?.id}
            show={dateVisible}
            onCancel={() => setDateVisible(false)}
            onConfirm={(date) => {
              drawerRoute.params?.onConfirm(date);
              setDateVisible(false);
            }}
          />
        )
      }
    </Fragment>
  );
};

export default LeftDrawerScreen;
